/**
 * Espansione RRULE lato client — genera occorrenze virtuali per la vista
 * calendario (settimana / mese) senza toccare il DB. Le task salvate NON
 * cambiano: il backend continua a restituire la "master" ricorrente + le
 * eventuali next-occurrence già materializzate da complete_task_with_recurrence.
 *
 * Perché frontend e non backend:
 * - il backend usa un pattern "spawn-on-complete" (task_ops.py) → l'API agenda
 *   restituisce solo la task-master; espandendo lato client si mostra la serie
 *   completa in vista mese/settimana SENZA nessuna migrazione DB e SENZA
 *   incrementare "cicli aperti" (Bug 4 non regredisce: cicli_summary lavora
 *   solo su record reali del DB).
 * - il click / drag / complete su un'occorrenza virtuale opera sull'`id`
 *   originale della master (id preservato) → il backend `complete_task_with_recurrence`
 *   spawna la NEXT come sempre.
 *
 * Supporto RRULE (subset sufficiente al Bug 5):
 *   FREQ=DAILY|WEEKLY|MONTHLY|YEARLY
 *   INTERVAL=<int>              (default 1)
 *   COUNT=<int>                 (limite dalla dtstart, incluso l'origine)
 *   UNTIL=YYYYMMDD[THHMMSS[Z]]  (data inclusiva)
 *   BYDAY=MO,TU,WE,TH,FR,SA,SU  (solo con FREQ=WEEKLY per il Bug 5)
 *
 * Le RRULE non parsabili → nessuna espansione (silenzioso, la master resta
 * come singola occorrenza). Cap di sicurezza: max 400 occorrenze generate.
 */

const WEEKDAY_MAP = { MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6 };
const MAX_OCCURRENCES = 400;

function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function parseIsoLocal(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Parse "20261115T235959Z" o "20261115" → Date locale (fine-giornata inclusiva). */
function parseUntil(v) {
  if (!v) return null;
  const s = v.replace(/[TZ]/g, "");
  if (s.length < 8) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6));
  const d = Number(s.slice(6, 8));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** parseRRule("FREQ=WEEKLY;INTERVAL=1;COUNT=3;BYDAY=MO,WE") → oggetto tipizzato. */
export function parseRRule(rule) {
  if (!rule || typeof rule !== "string") return null;
  const cleaned = rule.trim().replace(/^RRULE:/i, "");
  if (!cleaned) return null;
  const parts = cleaned.split(";").map((p) => p.trim()).filter(Boolean);
  const out = { freq: null, interval: 1, count: null, until: null, byday: null };
  for (const p of parts) {
    const [rawK, rawV] = p.split("=");
    if (!rawK || rawV === undefined) continue;
    const k = rawK.trim().toUpperCase();
    const v = rawV.trim();
    if (k === "FREQ") out.freq = v.toUpperCase();
    else if (k === "INTERVAL") out.interval = Math.max(1, parseInt(v, 10) || 1);
    else if (k === "COUNT") out.count = Math.max(1, parseInt(v, 10) || 0) || null;
    else if (k === "UNTIL") out.until = parseUntil(v);
    else if (k === "BYDAY") {
      out.byday = v.split(",")
        .map((d) => d.trim().toUpperCase().replace(/^[-+0-9]+/, ""))
        .filter((d) => d in WEEKDAY_MAP);
      if (out.byday.length === 0) out.byday = null;
    }
    // altre parti (BYMONTHDAY, BYSETPOS, WKST, ecc.) ignorate per il Bug 5.
  }
  if (!out.freq || !["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(out.freq)) return null;
  return out;
}

/** Weekday Mon=0 … Sun=6 (coerente con la UI settimanale italiana). */
function weekdayMonFirst(d) {
  return (d.getDay() + 6) % 7;
}

/** Genera tutte le occorrenze [dtstart .. cap] fino a `rangeEnd` (inclusivo). */
function generateOccurrences(dtstart, rrule, rangeStart, rangeEnd) {
  const out = [];
  if (!dtstart || !rrule) return out;
  const cap = rangeEnd;
  const untilCap = rrule.until && rrule.until < cap ? rrule.until : cap;

  const push = (d) => {
    if (out.length >= MAX_OCCURRENCES) return false;
    // Confronto per data pura (ignora ora)
    const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (dOnly > untilCap) return false;
    if (rrule.count && out.length >= rrule.count) return false;
    if (dOnly >= rangeStart) out.push(dOnly);
    return true;
  };

  // Origine: sempre "prodotta" ai fini del COUNT (RFC 5545), ma inclusa in `out`
  // solo se rientra nel range visibile.
  let produced = 0; // conta rispetto a COUNT
  const includeIfProduced = (d) => {
    produced += 1;
    if (rrule.count && produced > rrule.count) return false;
    const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (dOnly > untilCap) return false;
    if (dOnly >= rangeStart && dOnly <= untilCap) {
      out.push(dOnly);
      if (out.length >= MAX_OCCURRENCES) return false;
    }
    return true;
  };

  if (rrule.freq === "DAILY") {
    let cur = new Date(dtstart);
    while (true) {
      if (!includeIfProduced(cur)) break;
      cur = new Date(cur); cur.setDate(cur.getDate() + rrule.interval);
      if (cur > untilCap) break;
    }
  } else if (rrule.freq === "WEEKLY") {
    // Se BYDAY presente → per ogni settimana produci gli weekday elencati;
    // altrimenti replica lo stesso weekday di dtstart.
    const startOfWeek = new Date(dtstart);
    startOfWeek.setDate(dtstart.getDate() - weekdayMonFirst(dtstart)); // lunedì della settimana di dtstart
    let weekAnchor = startOfWeek;
    const byday = rrule.byday && rrule.byday.length > 0
      ? rrule.byday.map((d) => WEEKDAY_MAP[d])
      : [weekdayMonFirst(dtstart)];
    byday.sort((a, b) => a - b);

    outer: while (true) {
      for (const wd of byday) {
        const occ = new Date(weekAnchor);
        occ.setDate(weekAnchor.getDate() + wd);
        if (occ < dtstart) continue; // salta i giorni della prima settimana precedenti a dtstart
        if (!includeIfProduced(occ)) break outer;
      }
      const nextAnchor = new Date(weekAnchor);
      nextAnchor.setDate(nextAnchor.getDate() + 7 * rrule.interval);
      if (nextAnchor > untilCap) break;
      weekAnchor = nextAnchor;
    }
  } else if (rrule.freq === "MONTHLY") {
    let cur = new Date(dtstart);
    while (true) {
      if (!includeIfProduced(cur)) break;
      const nxt = new Date(cur);
      nxt.setMonth(nxt.getMonth() + rrule.interval);
      if (nxt > untilCap) break;
      cur = nxt;
    }
  } else if (rrule.freq === "YEARLY") {
    let cur = new Date(dtstart);
    while (true) {
      if (!includeIfProduced(cur)) break;
      const nxt = new Date(cur);
      nxt.setFullYear(nxt.getFullYear() + rrule.interval);
      if (nxt > untilCap) break;
      cur = nxt;
    }
  }

  return out;
}

/**
 * Espande un array di task nel range `[startIso, endIso]`.
 * - Task senza `recurrence_rule` → passthrough.
 * - Task con `recurrence_rule` → genera occorrenze virtuali; l'occorrenza che
 *   coincide con `scheduled_date`/`due_date` reale della master viene resa
 *   con l'oggetto master originale (id vero). Le altre sono cloni con
 *   `is_recurring_instance: true` e `_key` unico (id non cambia — cliccando
 *   si apre la master originale e il complete opera sulla master).
 *
 * Dedup: se due record ricorrenti materializzati (master + spawned next)
 * cadono sulla stessa data → priorità al record reale con id (senza clone).
 */
export function expandTasksInRange(tasks, startIso, endIso) {
  if (!Array.isArray(tasks)) return [];
  const rangeStart = parseIsoLocal(startIso);
  const rangeEnd = parseIsoLocal(endIso);
  if (!rangeStart || !rangeEnd) return tasks;

  // occ index: (parentGroupKey, ISO) → task già in output → dedup
  const seen = new Map();
  const groupKey = (t) => (t.recurrence_parent_id || t.id || "");
  const out = [];

  const putReal = (t) => {
    const iso = t.scheduled_date || t.due_date;
    if (!iso) { out.push(t); return; }
    const key = `${groupKey(t)}::${iso}`;
    if (seen.has(key)) return; // dedup master vs spawned figlia sulla stessa data
    seen.set(key, true);
    out.push(t);
  };

  const putVirtual = (t, occIso) => {
    const key = `${groupKey(t)}::${occIso}`;
    if (seen.has(key)) return; // già coperta da un record reale
    seen.set(key, true);
    const clone = { ...t };
    // Sostituisci la data della master con quella dell'occorrenza virtuale
    if (t.scheduled_date) clone.scheduled_date = occIso;
    else clone.due_date = occIso;
    clone.is_recurring_instance = true;
    // React key univoca — l'id REALE resta invariato, così complete/edit vanno
    // alla master (comportamento voluto: spawn-on-complete).
    clone._key = `${t.id}::occ::${occIso}`;
    out.push(clone);
  };

  for (const t of tasks) {
    if (!t) continue;
    if (!t.recurrence_rule) { putReal(t); continue; }
    // Task ricorrente — occorrenza reale (master o spawned) first
    putReal(t);
    const dtIso = t.scheduled_date || t.due_date;
    if (!dtIso) continue;
    const dtstart = parseIsoLocal(dtIso);
    const rrule = parseRRule(t.recurrence_rule);
    if (!rrule) continue;
    const occs = generateOccurrences(dtstart, rrule, rangeStart, rangeEnd);
    for (const occ of occs) {
      const occIso = toIso(occ);
      // Se coincide con la data della master → già inserito da putReal
      if (occIso === dtIso) continue;
      putVirtual(t, occIso);
    }
  }
  return out;
}
